import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  ArrowRight,
  BarChart3,
  FileText,
  PackageCheck,
  Pill,
  ShieldCheck,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { reportsService } from '@/services/reports';
import { useAuthStore } from '@/stores/authStore';
import { KpiCard, PageHeader } from '@/components/ui';
import { localeFor } from '@/components/reports/chartTheme';

interface Section {
  key: string;
  path: string;
  Icon: LucideIcon;
  tone: string;
  adminOnly?: boolean;
}

const SECTIONS: Section[] = [
  { key: 'users', path: '/admin/users', Icon: UserCog, tone: 'bg-primary-50 text-primary-700', adminOnly: true },
  { key: 'drugs', path: '/admin/drugs', Icon: Pill, tone: 'bg-success-50 text-success-700' },
  { key: 'audit', path: '/admin/audit', Icon: ShieldCheck, tone: 'bg-navy-50 text-navy-700' },
  { key: 'reports', path: '/admin/reports', Icon: BarChart3, tone: 'bg-warning-50 text-warning-700' },
  { key: 'health', path: '/admin/health', Icon: Activity, tone: 'bg-gray-100 text-gray-600' },
];

export function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const isAdmin = useAuthStore((s) => s.user?.role) === 'ADMIN';
  const locale = localeFor(i18n.language);

  // /reports/admin/stats is ADMIN-only; auditors would just get a 403.
  const { data: stats, isLoading, isError } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => reportsService.getAdminStats(),
    enabled: isAdmin,
  });

  const fmt = (value: number | undefined) =>
    isLoading ? '…' : isError || value == null ? '—' : value.toLocaleString(locale);

  const sections = SECTIONS.filter((s) => isAdmin || !s.adminOnly);

  return (
    <div>
      <PageHeader title={t('admin.dashboard.title')} description={t('admin.dashboard.description')} />

      {isAdmin && (
        <>
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard label={t('admin.dashboard.activeUsers')} value={fmt(stats?.activeUsers)} icon={Users} tone="primary" to="/admin/users" />
            <KpiCard
              label={t('admin.dashboard.prescriptionsThisMonth')}
              value={fmt(stats?.prescriptionsThisMonth)}
              icon={FileText}
              tone="navy"
              to="/admin/reports"
            />
            <KpiCard
              label={t('admin.dashboard.fulfillmentsThisMonth')}
              value={fmt(stats?.fulfillmentsThisMonth)}
              icon={PackageCheck}
              tone="success"
              to="/admin/reports"
            />
            <KpiCard label={t('admin.dashboard.totalDrugs')} value={fmt(stats?.totalDrugs)} icon={Pill} tone="warning" to="/admin/drugs" />
          </div>
          {isError && <p className="-mt-6 mb-8 text-sm text-danger-700">{t('admin.dashboard.statsError')}</p>}
        </>
      )}

      <h2 className="section-title mb-3">{t('admin.dashboard.sections')}</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sections.map(({ key, path, Icon, tone }) => (
          <Link key={key} to={path} className="card card-interactive group flex items-start gap-4 p-5">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tone}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900">{t(`admin.dashboard.cards.${key}.title`)}</h3>
              <p className="mt-0.5 text-sm text-gray-500">{t(`admin.dashboard.cards.${key}.description`)}</p>
            </div>
            <ArrowRight className="rtl-flip mt-0.5 h-4 w-4 shrink-0 text-gray-300 transition-colors group-hover:text-primary-600" />
          </Link>
        ))}
      </div>
    </div>
  );
}
